import { SortingState } from "@tanstack/react-table";
import { Constants } from "appConstants";
import { IcoArrowDown } from "assets/icons";
import { HeadingListing, Loading, Table } from "components";

import TableHelper from "helpers/TableHelper";
import { useQuerystringParams } from "hooks/querystringFilter";
import { Tenant } from "models/tenant/Tenant";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TenantService } from "services";
import { useTenant } from "store/contextTenant";
import { truncateTextWithEllipsis } from "utils";
import "../styles.scss";
import { TenantFilters } from "./types";

export default function TenantList() {
	const navigate = useNavigate();
	const [tenants, setTenants] = useState<Tenant[] | undefined>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [searchName, setSearchName] = useState<string>("");
	const contextTenant = useTenant();

	const tenantColumns: any[] = [
		{
			header: Constants.strings.pages.listing.tenant.headers.product,
			accessorKey: "name",
			meta: {
				columnClass: "name-column"
			},
			size: 35
		},
		{
			header: Constants.strings.pages.listing.tenant.headers.type,
			accessorKey: "type",
			size: 25.5
		},
		{
			header: () =>
				Constants.strings.pages.listing.tenant.headers.productionLink,
			accessorKey: "url",
			cell: (rowData: any) => (
				<a
					href={rowData.getValue()}
					className="hover:underline"
					onClick={(e) => e.stopPropagation()}
					target="_blank"
					rel="noreferrer"
				>
					{rowData?.getValue()
						? truncateTextWithEllipsis(rowData.getValue(), 35)
						: "n/a"}
				</a>
			),
			size: 17.5
		},
		{
			header: () => Constants.strings.pages.listing.tenant.headers.homologLink,
			accessorKey: "homologUrl",
			cell: (rowData: any) => (
				<a
					href={rowData.getValue()}
					className="hover:underline"
					onClick={(e) => e.stopPropagation()}
					target="_blank"
					rel="noreferrer"
				>
					{rowData?.getValue()
						? truncateTextWithEllipsis(rowData.getValue(), 35)
						: "n/a"}
				</a>
			),
			size: 17.5
		},
		{
			header: "",
			accessorKey: "id",
			cell: (
				<div className="text-align--center align-items--center arrow-right">
					<IcoArrowDown />
				</div>
			),
			enableSorting: false,
			size: 5.5
		}
	];

	const [filters, setFilters] = useState<any>({
		size: 10,
		page: 0,
		name: searchName
	});

	const tenantService = new TenantService();

	const [sorting, setSorting] = useState<SortingState>([]);
	const { updateUrl } = useQuerystringParams<TenantFilters>(
		setFilters,
		sorting,
		setSorting
	);

	const parseParams = () => {
		const sortParam = TableHelper.sortingStateToParam(sorting);
		const filtersParams = {
			...filters,
			name: filters.search
		};
		if ("search" in filtersParams) {
			delete filtersParams.search;
		}
		return {
			...sortParam,
			...filtersParams
		};
	};

	const onPageChange = (selectedPage: number, pageSize: number) => {
		const newFilters = {
			...filters,
			size: pageSize,
			page: selectedPage - 1
		};
		setFilters(newFilters);
	};

	useEffect(() => {
		const newFilters = {
			...filters,
			name: searchName
		};
		setFilters(newFilters);
	}, [searchName]);

	useEffect(() => {
		const sortParam = TableHelper.sortingStateToParam(sorting);
		const newFilters = {
			...filters,
			...sortParam
		};
		updateUrl(newFilters);
	}, [sorting]);

	useEffect(() => {
		setIsLoading(true);
		tenantService
			.getList(parseParams())
			.then((response: any) => {
				setTenants(response.content);
				setTotalPages(response.totalPages);

				if (response.content) {
					if (response.content.length !== 0 && !contextTenant.tenant) {
						contextTenant.setTenant({
							label: response.content[0].name,
							value: response.content[0].id,
							text: ""
						});
					}
				}
			})
			.catch(() => setTenants(undefined))
			.finally(() => setIsLoading(false));
	}, [filters]);

	return (
		<div className="tenant-page">
			<HeadingListing
				title={Constants.strings.general.tenants}
				setSearchValue={setSearchName}
			/>
			{!isLoading ? (
				<Table
					columns={tenantColumns}
					data={tenants as Tenant[]}
					sorting={sorting}
					setSorting={setSorting}
					numberOfInstancesOptions={[10, 50, 100]}
					currentPage={filters.page + 1}
					pageSize={filters.size}
					totalPages={totalPages}
					onPageChange={onPageChange}
					onClickRow={(id: string) => navigate(`/backoffice/tenants/${id}`)}
				/>
			) : (
				<div className="tenant-list-loading">
					<Loading type="primary" size={75} />
				</div>
			)}
		</div>
	);
}
